<template>
  <div class="gems-panel">
    <div class="gems-top">
      <div class="gems-balance">
        <img
          class="gems-art"
          src="/assets/gifting/gems-illustration.svg"
          alt="Wallet"
        />
        <span class="gems-balance-label">Wallet Balance</span>
        <strong class="gems-balance-value">
          <span class="gems-currency">Ð</span>{{
            balanceLoaded ? formatAmount(balance) : "—"
          }}
        </strong>
        <p class="gems-hint">
          <span class="gems-hint-icon">i</span>
          <span>
            Use your balance to convert for gems or to withdraw balance use
            <a
              href="https://mywhoosh.com/getting-started-mywhoosh-cycling-app/"
              target="_blank"
              rel="noopener"
              >Link App</a
            >
          </span>
        </p>
      </div>

      <div class="gems-convert">
        <h3>Convert to Gems</h3>

        <div class="convert-rows">
          <div class="convert-row">
            <img src="/assets/gifting/symbol-gem.svg" alt="" />
            <div class="convert-field">
              <label for="convert-gems">Receive</label>
              <input
                id="convert-gems"
                :value="gemsInput"
                type="text"
                inputmode="numeric"
                placeholder="0"
                :disabled="!balanceLoaded || !gemsPerAed"
                @input="onGemsInput"
              />
            </div>
            <span class="convert-unit">GEM</span>
          </div>

          <span class="convert-swap">
            <img src="/assets/gifting/swap-circle.svg" alt="" />
          </span>

          <div class="convert-row">
            <img src="/assets/gifting/symbol-aed.svg" alt="" />
            <div class="convert-field">
              <label for="convert-amount">Convert</label>
              <input
                id="convert-amount"
                :value="aedDisplay"
                type="text"
                placeholder="0"
                readonly
                tabindex="-1"
              />
            </div>
            <span class="convert-unit">AED</span>
          </div>
        </div>

        <div class="convert-pills">
          <span class="convert-pill">
            <img src="/assets/gifting/icon-zero-fee.svg" alt="" />
            Zero Fee
          </span>
          <span class="convert-pill">
            <img src="/assets/gifting/gem-mini.svg" alt="" />
            1 AED = {{ rateLabel }} GEM
          </span>
        </div>

        <p v-if="error" class="convert-error">{{ error }}</p>

        <b-button
          class="convert-cta"
          type="button"
          :disabled="!canConvert || converting"
          @click="convert"
        >
          {{ converting ? "Converting…" : "Convert" }}
        </b-button>
      </div>
    </div>

    <div class="gems-txns">
      <div class="gems-txns-head">
        <h3>Recent Transactions</h3>
      </div>

      <div class="gems-table-scroll">
        <table class="gems-table">
          <thead>
            <tr>
              <th>Details</th>
              <th>Date &amp; Time</th>
              <th>Gems</th>
              <th>Amount (AED)</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="txn in transactions" :key="txn.id">
              <td>{{ txn.details }}</td>
              <td class="txn-date">{{ formatDate(txn.at) }}</td>
              <td class="txn-gems">
                {{ txn.gems ? `+${txn.gems} Gems` : "—" }}
              </td>
              <td class="txn-amount">AED {{ formatAmount(txn.amount) }}</td>
              <td class="txn-status">{{ txn.status }}</td>
            </tr>
            <tr v-if="!transactions.length && !transactionsFailed">
              <td class="txn-empty" colspan="5">
                Your wallet activity will appear here.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="hasMoreTransactions" class="gems-load-more">
        <b-button
          type="button"
          class="btn-load-more"
          :disabled="loadingMore"
          @click="loadMore"
        >
          {{ loadingMore ? "Loading…" : "Load more" }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
const PAGE_SIZE = 4;

export default {
  name: "GiftingGems",
  data() {
    return {
      balance: 0,
      balanceLoaded: false,
      transactions: [],
      gemsInput: "",
      error: "",
      converting: false,
      page: 1,
      hasMoreTransactions: false,
      loadingMore: false,
      transactionsFailed: false,
      transactionsGeneration: 0,
    };
  },
  computed: {
    ...mapGetters("gifting", ["gemRate"]),
    gemsPerAed() {
      return Number(this.gemRate?.gems_per_aed) || 0;
    },
    rateLabel() {
      return this.gemsPerAed ? String(this.gemsPerAed) : "—";
    },
    maxGems() {
      if (!this.gemsPerAed) return 0;
      const most = this.gemsFor(this.balance);
      return this.aedFor(most) > this.balance ? most - 1 : most;
    },
    parsedGems() {
      const value = parseInt(this.gemsInput, 10);
      return Number.isFinite(value) && value > 0 ? value : 0;
    },
    amountToSend() {
      return this.aedFor(this.parsedGems);
    },
    aedDisplay() {
      return this.parsedGems ? String(this.amountToSend) : "";
    },
    canConvert() {
      return (
        this.balanceLoaded &&
        this.parsedGems > 0 &&
        this.amountToSend > 0 &&
        this.amountToSend <= this.balance
      );
    },

  },
  methods: {
    async loadTransactions(page) {
      const generation =
        page === 1 ? ++this.transactionsGeneration : this.transactionsGeneration;

      const res = await this.$store.dispatch("gifting/getGiftingTransactions", {
        page,
        per_page: PAGE_SIZE,
      });

      if (generation !== this.transactionsGeneration) return;

      if (!res?.status) {
        if (page === 1) this.transactionsFailed = true;
        return;
      }

      const incoming = res.data.transactions;

      this.transactionsFailed = false;
      this.transactions =
        page === 1
          ? incoming
          : [
              ...this.transactions,
              ...incoming.filter(
                (txn) => !this.transactions.some((row) => row.id === txn.id)
              ),
            ];
      this.page = res.data.page;
      this.hasMoreTransactions = res.data.has_more;
    },
    async loadMore() {
      this.loadingMore = true;
      await this.loadTransactions(this.page + 1);
      this.loadingMore = false;
    },
    formatAmount(value) {
      return Number(value || 0).toFixed(2);
    },
    formatDate(value) {
      const date = new Date(String(value).replace(" ", "T"));
      return `${date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}, ${date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`;
    },
    gemsFor(amount) {
      return this.gemsPerAed
        ? Math.floor(Number((amount * this.gemsPerAed).toFixed(6)))
        : 0;
    },
    aedFor(gems) {
      if (!gems || !this.gemsPerAed) return 0;
      let fils = Math.ceil(Number(((gems / this.gemsPerAed) * 100).toFixed(6)));
      if (this.gemsFor(fils / 100) < gems) fils += 1;
      return fils / 100;
    },
    onGemsInput(event) {
      const input = event.target;
      const raw = input.value
        .split(/[.,]/)[0]
        .replace(/\D/g, "")
        .replace(/^0+(?=\d)/, "");

      this.error = "";

      if (raw && Number(raw) > this.maxGems) {
        this.error = this.maxGems
          ? `You can convert up to ${this.maxGems} gems.`
          : "You do not have enough balance to convert.";
      }

      this.gemsInput = raw;
      if (input.value !== raw) input.value = raw;
    },
    async convert() {
      if (!this.canConvert || this.converting) {
        this.error = "Enter the gems you want to convert.";
        return;
      }

      this.converting = true;
      this.error = "";

      const res = await this.$store.dispatch("gifting/convertToGems", {
        amount: this.amountToSend,
      });

      this.converting = false;

      if (!res?.status) {
        const latest = await this.$store.dispatch("gifting/getGiftingBalance");
        if (latest?.status) this.balance = Number(latest.data.balance) || 0;
        return;
      }

      this.balance = Number(res.data.balance) || 0;
      this.gemsInput = "";
      this.$store.dispatch("user/get_rider_default_data");
      await this.loadTransactions(1);
    },
  },
  async created() {
    this.$store.dispatch("gifting/getGemRate");
    this.loadTransactions(1);

    const res = await this.$store.dispatch("gifting/getGiftingBalance");

    if (res?.status) {
      this.balance = Number(res.data.balance) || 0;
      this.balanceLoaded = true;
    } else {
      this.error = "We could not load your balance. Please try again shortly.";
    }
  },
};
</script>

<style scoped>
.gems-top {
  background-color: #0f1012;
  border-radius: 14px;
  display: grid;
  gap: 0;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;
  padding: 8px;
}

.gems-balance {
  padding: 26px 24px 30px;
  text-align: center;
}

.gems-art {
  display: block;
  height: 190px;
  margin: 0 auto 18px;
  max-width: 100%;
  object-fit: contain;
}

.gems-balance-label {
  color: var(--gift-gold);
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

.gems-balance-value {
  color: #ffffff;
  display: block;
  font-size: 44px;
  font-weight: 700;
  line-height: 1.2;
  margin: 6px 0 18px;
}

.gems-currency {
  margin-right: 10px;
}

.gems-hint {
  align-items: flex-start;
  background-color: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  color: #9ca3af;
  display: flex;
  font-size: 14px;
  gap: 12px;
  line-height: 1.5;
  margin: 0 auto;
  max-width: 360px;
  padding: 14px 16px;
  text-align: left;
}

.gems-hint-icon {
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #d4d4d8;
  display: flex;
  flex: 0 0 auto;
  font-size: 13px;
  font-style: italic;
  height: 30px;
  justify-content: center;
  width: 30px;
}

.gems-hint a {
  color: var(--gift-gold);
  text-decoration: none;
}

.gems-convert {
  background-color: #f2f2f3;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  padding: 26px 22px 24px;
}

.gems-convert h3 {
  color: var(--gift-ink);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.3px;
  margin-bottom: 30px;
  text-align: center;
  text-transform: uppercase;
}

.convert-rows {
  position: relative;
}

.convert-row {
  align-items: center;
  background-color: #ffffff;
  border: 1px solid #ededed;
  border-radius: 14px;
  display: flex;
  gap: 16px;
  min-height: 76px;
  padding: 12px 18px;
}

.convert-row + .convert-row {
  margin-top: 10px;
}

.convert-row > img {
  flex: 0 0 auto;
  height: 48px;
  width: 48px;
}

.convert-field {
  flex: 1 1 auto;
  min-width: 0;
}

.convert-field label {
  color: #9ca3af;
  display: block;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.4px;
  margin: 0 0 2px;
  text-transform: uppercase;
}

.convert-field input {
  background: transparent;
  border: 0;
  color: var(--gift-ink);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  outline: none;
  padding: 0;
  width: 100%;
}

.convert-unit {
  align-items: center;
  align-self: stretch;
  border-left: 1px solid #e5e7eb;
  color: #9ca3af;
  display: flex;
  flex: 0 0 auto;
  font-size: 15px;
  font-weight: 500;
  justify-content: center;
  margin: 12px 0;
  width: 86px;
}

.convert-swap {
  align-items: center;
  background-color: #f2f2f3;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #e8e8e8;
  display: flex;
  height: 52px;
  justify-content: center;
  left: 50%;
  line-height: 0;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 52px;
  z-index: 1;
}

.convert-swap img {
  border: 0;
  display: block;
  height: 36px;
  max-width: none;
  width: 36px;
}

.convert-pills {
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-top: 16px;
}

.convert-pill {
  align-items: center;
  background-color: #ffffff;
  border-radius: 999px;
  color: var(--gift-ink);
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  gap: 8px;
  letter-spacing: 0.6px;
  padding: 8px 16px;
  text-transform: uppercase;
}

.convert-pill img {
  flex: 0 0 auto;
  height: 16px;
  width: 16px;
}

.convert-pill:last-child img {
  height: 16px;
  width: 18px;
}

.convert-error {
  color: #a3352b;
  font-size: 13px;
  margin: 14px 0 0;
  text-align: center;
}

.convert-cta {
  background-image: var(--gift-gradient);
  border: 0;
  border-radius: 999px;
  color: var(--gift-ink);
  font-size: 17px;
  font-weight: 700;
  height: 56px;
  letter-spacing: 0.6px;
  margin-top: auto;
  min-height: 56px;
  text-transform: uppercase;
  width: 100%;
}

.convert-cta:disabled {
  background-image: none;
  background-color: var(--gift-disabled-bg);
  color: var(--gift-disabled-ink);
  opacity: 1;
}

.gems-txns {
  background-color: #ffffff;
  border: 1px solid #ededed;
  border-radius: 14px;
  margin-top: 26px;
  padding: 24px;
}

.gems-txns-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.gems-txns-head h3 {
  color: var(--gift-ink);
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.4px;
  margin: 0;
  text-transform: uppercase;
}

.gems-load-more {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.btn-load-more {
  align-items: center;
  background-color: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  color: var(--gift-ink);
  display: inline-flex;
  font-size: 14px;
  font-weight: 700;
  height: 42px;
  justify-content: center;
  letter-spacing: 0.5px;
  line-height: 1;
  padding: 0 28px;
  text-transform: uppercase;
}

.btn-load-more:hover,
.btn-load-more:focus {
  background-color: var(--gift-gold);
  border-color: var(--gift-gold);
  color: var(--gift-ink);
}

.gems-table-scroll {
  overflow-x: auto;
}

.gems-table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 720px;
  width: 100%;
}

.gems-table th {
  background-color: #1a1d21;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  padding: 14px 16px;
  text-transform: uppercase;
  white-space: nowrap;
}

.gems-table th:first-child {
  border-radius: 8px 0 0 8px;
}

.gems-table th:last-child {
  border-radius: 0 8px 8px 0;
}

.gems-table th:not(:first-child),
.gems-table td:not(:first-child) {
  text-align: center;
}

.gems-table td {
  border-bottom: 1px solid #f0f0f0;
  color: var(--gift-ink);
  font-size: 15px;
  padding: 18px 16px;
  white-space: nowrap;
}

.gems-table td.txn-date {
  color: #64748b;
}

.gems-table td.txn-gems,
.gems-table td.txn-status {
  color: #00a849;
  font-weight: 700;
}

.txn-amount {
  font-weight: 700;
}

.txn-empty {
  color: #9ca3af;
  text-align: center !important;
}

@media (max-width: 991px) {
  .gems-top {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .convert-row {
    gap: 10px;
    padding: 10px 12px;
  }

  .convert-row > img {
    height: 36px;
    width: 36px;
  }

  .convert-field input {
    font-size: 22px;
  }

  .convert-unit {
    width: 56px;
  }
}
</style>
